import type { Enemy } from './enemies';

/** 玩家飞行道具：法杖弹（可追踪）/ 哨箭（穿透直线）。 */
export type BoltKind = 'staff' | 'arrow';

export interface Bolt {
  active: boolean;
  kind: BoltKind;
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  vx: number;
  vy: number;
  damage: number;
  size: number;
  /** 剩余可穿透敌数（staff 通常 0，arrow 大值）。 */
  pierce: number;
  /** 已命中敌人 id（穿透不重复命中）。 */
  hits: number[];
  /** 追踪强度（0=直线）。 */
  homing: number;
  targetId: number;
  spin: number;
}

export function createBolt(): Bolt {
  return {
    active: false, kind: 'staff',
    x: 0, y: 0, prevX: 0, prevY: 0,
    vx: 0, vy: 0, damage: 0, size: 8,
    pierce: 0, hits: [], homing: 0, targetId: -1, spin: 0,
  };
}

export function resetBolt(b: Bolt): void {
  b.hits.length = 0;
  b.targetId = -1;
}

export interface SpawnBoltOpts {
  kind: BoltKind;
  x: number;
  y: number;
  angle: number;
  speed: number;
  damage: number;
  size: number;
  pierce: number;
  homing: number;
  targetId: number;
}

/** 从池中取弹并初始化（控制器调用）。 */
export function spawnBolt(
  pool: { obtain(): Bolt },
  o: SpawnBoltOpts,
): Bolt {
  const b = pool.obtain();
  resetBolt(b);
  b.active = true;
  b.kind = o.kind;
  b.x = o.x; b.y = o.y;
  b.prevX = o.x; b.prevY = o.y;
  b.vx = Math.cos(o.angle) * o.speed;
  b.vy = Math.sin(o.angle) * o.speed;
  b.damage = o.damage;
  b.size = o.size;
  b.pierce = o.pierce;
  b.homing = o.homing;
  b.targetId = o.targetId;
  b.spin = 0;
  return b;
}

function steer(b: Bolt, tx: number, ty: number, dt: number): void {
  const tl = Math.hypot(tx, ty);
  if (tl < 1e-6) return;
  const speed = Math.hypot(b.vx, b.vy);
  const desiredX = (tx / tl) * speed;
  const desiredY = (ty / tl) * speed;
  const k = Math.min(1, b.homing * dt);
  b.vx += (desiredX - b.vx) * k;
  b.vy += (desiredY - b.vy) * k;
}

function findTarget(b: Bolt, enemies: readonly Enemy[]): Enemy | null {
  let best: Enemy | null = null;
  let bd = 1e9;
  for (const e of enemies) {
    if (!e.active) continue;
    const d = Math.hypot(e.x - b.x, e.y - b.y);
    if (d < bd) { bd = d; best = e; }
  }
  return best;
}

/** 道具移动；返回 false 表示飞出过远应回收（由控制器判定边界）。 */
export function updateBolt(b: Bolt, dt: number, enemies: readonly Enemy[]): void {
  b.prevX = b.x;
  b.prevY = b.y;
  if (b.homing > 0) {
    let t: Enemy | null = null;
    for (const e of enemies) {
      if (e.active && e.id === b.targetId) { t = e; break; }
    }
    if (!t) t = findTarget(b, enemies);
    if (t) {
      b.targetId = t.id;
      steer(b, t.x - b.x, t.y - b.y, dt);
    }
  }
  b.x += b.vx * dt;
  b.y += b.vy * dt;
  b.spin += dt * 10;
}
